// src/components/admin/lead-detail/UpdateLeadStatusDialog.js

import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Button } from "../../../../components/ui/button";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { updateLeadStatus } from "../../../../services/AdminApis"; // Adjust path if needed
import { useToast } from "../../../../hooks/use-toast";

const LEAD_STATUS_OPTIONS = [
  "RELEASED",
  "CLOSED",
  "MODERATED",
  "HIRED",
];
const REASON_OPTIONS = [
  "REQUIREMENT_CLOSED",
  "INVALID_CONTACT",
  "INVALID_LEAD_INFO",
  "ABUSIVE_BUYER",
];

export const UpdateLeadStatusDialog = ({
  leadId,
  currentStatus,
  onUpdateSuccess,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newStatus, setNewStatus] = useState(currentStatus);
  const [reason, setReason] = React.useState(""); // State for reason
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newStatus === currentStatus) {
      toast({
        variant: "destructive",
        title: "No Change",
        description: "The new status is the same as the current status.",
      });
      return;
    }

    setIsLoading(true);
    try {
      // 3. Add reason to the payload
      const payload = { new_status: newStatus, notes, reason: reason || null };
      const updatedLead = await updateLeadStatus(leadId, payload);
      toast({
        title: "Success",
        description: `Lead status updated to ${newStatus}.`,
        variant: "success",
      });
      onUpdateSuccess(updatedLead);
      setIsOpen(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Update Failed",
        description:
          error.response?.data?.detail || "An unexpected error occurred.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>Change Status</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Update Lead Status</DialogTitle>
            <DialogDescription>
              Select a new status and add notes for the change. This action will
              be logged.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">
                Status
              </Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select a status" />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* 4. Add the new "Reason" dropdown */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="reason" className="text-right">
                Reason
              </Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select a reason (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {REASON_OPTIONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="notes" className="text-right">
                Notes
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="col-span-3"
                placeholder="Reason for the status change (optional)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Updating..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
